import XLSX from 'xlsx/dist/xlsx.full.min.js';
import { getLotoTypeLabelWithFilter } from '../helper/lotoType';
import { getWalletLabel } from '../helper/wallet';

const formatNumber = (num) => {
  return new Intl.NumberFormat('vi-VN').format(num || 0);
};

const formatCurrency = (num) => {
  return new Intl.NumberFormat('vi-VN').format(num || 0) + ' đ';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const getWinningStatusText = (status) => {
  switch (status) {
    case 4:
      return 'Trúng thưởng';
    case 5:
      return 'Không trúng';
    default:
      return 'Chưa tính giải';
  }
};

const getRegionText = (region) => {
  switch (region) {
    case 'MB':
      return 'Miền Bắc';
    case 'MN':
      return 'Miền Nam';
    case 'MT':
      return 'Miền Trung';
    default:
      return region || '';
  }
};

const getTicketTypeText = (ticketType) => {
  switch (ticketType) {
    case 'single':
      return 'Vé đơn';
    case 'pair':
      return 'Cặp nguyên';
    default:
      return ticketType || 'Vé đơn';
  }
};

const formatNumbersWithSpace = (numbers) => {
  if (!numbers) return '';
  return numbers.split('').join(' ');
};

const SUMMARY_DIVIDER = '═══════════════════════════════════════════════════════';

const formatFilterRange = (from, to) => {
  const fromText = from ? new Date(from).toLocaleDateString('vi-VN') : '';
  const toText = to ? new Date(to).toLocaleDateString('vi-VN') : '';
  if (fromText && toText) return `Từ ${fromText} đến ${toText}`;
  return fromText || toText || 'Tất cả';
};

const buildReportSheet = (title, infoRows, stats) => {
  const rows = [
    [title],
    [],
    ...infoRows,
    [],
    [SUMMARY_DIVIDER],
    ['THỐNG KÊ TỔNG QUAN'],
    [SUMMARY_DIVIDER],
    [],
    ...stats,
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 28 }, { wch: 25 }];

  const dividerRow = 2 + infoRows.length + 1;
  ws['!merges'] = [0, dividerRow, dividerRow + 1, dividerRow + 2].map((r) => ({
    s: { r, c: 0 },
    e: { r, c: 1 },
  }));

  return ws;
};

const buildDataSheet = (columns, rows) => {
  const ws = XLSX.utils.aoa_to_sheet([columns.map((col) => col.label), ...rows]);
  ws['!cols'] = columns.map((col) => ({ wch: col.width }));
  return ws;
};

const writeWorkbook = (wb, fileNamePrefix) => {
  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  XLSX.writeFile(wb, `${fileNamePrefix}_${dateStr}.xlsx`);
};

const applyHeaderStyle = (ws, range) => {
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const address = XLSX.utils.encode_cell({ r: range.s.r, c: C });
    if (!ws[address]) continue;
    ws[address].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '4472C4' } },
      alignment: { horizontal: 'center', vertical: 'center' }
    };
  }
};

export const exportUnmarketableTicketsToExcel = (data, summary, filter) => {
  const wb = XLSX.utils.book_new();

  const today = new Date().toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const timeRange = filter.fromDate && filter.toDate 
    ? `Từ ${filter.fromDate} đến ${filter.toDate}` 
    : filter.fromDate 
      ? `Từ ${filter.fromDate}` 
      : filter.toDate 
        ? `Đến ${filter.toDate}` 
        : 'Tất cả thời gian';

  const summaryData = [
    ['BÁO CÁO THỐNG KÊ VÉ Ế - XỔ SỐ TRUYỀN THỐNG'],
    [],
    ['Ngày xuất báo cáo:', today],
    ['Thời gian lọc:', timeRange],
    [],
    ['═══════════════════════════════════════════════════════'],
    ['THỐNG KÊ TỔNG QUAN'],
    ['═══════════════════════════════════════════════════════'],
    [],
    ['Chỉ tiêu', 'Số lượng vé', 'Tổng SL', 'Giá trị thưởng'],
    ['Tổng vé ế', formatNumber(summary.total), formatNumber(summary.totalAmount), formatCurrency(summary.totalRewardAmount)],
    [],
    ['Trúng thưởng', formatNumber(summary.wonCount), formatNumber(summary.wonAmount), formatCurrency(summary.wonRewardAmount)],
    ['Không trúng', formatNumber(summary.notWonCount), formatNumber(summary.notWonAmount), '-'],
    ['Chờ tính thưởng', formatNumber(summary.pendingCount), formatNumber(summary.pendingAmount), '-'],
    [],
    ['═══════════════════════════════════════════════════════'],
    ['TỶ LỆ THỐNG KÊ'],
    ['═══════════════════════════════════════════════════════'],
    [],
    ['Tỷ lệ trúng thưởng:', summary.total > 0 ? ((summary.wonCount / summary.total) * 100).toFixed(2) + '%' : '0%'],
    ['Tỷ lệ không trúng:', summary.total > 0 ? ((summary.notWonCount / summary.total) * 100).toFixed(2) + '%' : '0%'],
    ['Tỷ lệ chờ xử lý:', summary.total > 0 ? ((summary.pendingCount / summary.total) * 100).toFixed(2) + '%' : '0%'],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  
  wsSummary['!cols'] = [
    { wch: 25 },
    { wch: 18 },
    { wch: 18 },
    { wch: 22 },
  ];
  
  wsSummary['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
    { s: { r: 5, c: 0 }, e: { r: 5, c: 3 } },
    { s: { r: 6, c: 0 }, e: { r: 6, c: 3 } },
    { s: { r: 7, c: 0 }, e: { r: 7, c: 3 } },
    { s: { r: 16, c: 0 }, e: { r: 16, c: 3 } },
    { s: { r: 17, c: 0 }, e: { r: 17, c: 3 } },
    { s: { r: 18, c: 0 }, e: { r: 18, c: 3 } },
  ];

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Thống kê tổng quan');

  const headers = [
    'STT',
    'Ngày quay số',
    'Ngày tạo',
    'Miền',
    'Đài',
    'Loại vé',
    'Dãy số',
    'Dãy số (có khoảng cách)',
    'Số lượng',
    'Trạng thái',
    'Giải trúng',
    'Giá trị giải thưởng (đ)',
    'Ảnh vé',
  ];

  const dataRows = data.map((item, index) => {
    const region = item.ClassicLotteryChannel?.region || item.ClassicLotteryStorage?.region || '';
    const numbers = item.ClassicLotteryStorage?.numbers || '';
    
    return [
      index + 1,
      item.ClassicResult?.resultDate || '',
      formatDate(item.createdAt),
      getRegionText(region),
      item.ClassicLotteryChannel?.name || '',
      getTicketTypeText(item.ClassicLotteryStorage?.ticketType),
      numbers,
      formatNumbersWithSpace(numbers),
      item.amount || 0,
      getWinningStatusText(item.status),
      item.status === 4 ? parseRewardsForExcel(item.rewardNumbers, region) : '-',
      item.rewardAmount || 0,
      item.ClassicLotteryStorage?.images || '',
    ];
  });

  const wsData = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

  wsData['!cols'] = [
    { wch: 6 },
    { wch: 14 },
    { wch: 18 },
    { wch: 12 },
    { wch: 18 },
    { wch: 12 },
    { wch: 12 },
    { wch: 18 },
    { wch: 10 },
    { wch: 15 },
    { wch: 35 },
    { wch: 22 },
    { wch: 50 },
  ];

  XLSX.utils.book_append_sheet(wb, wsData, 'Chi tiết vé ế');

  const wonData = data.filter(item => item.status === 4);
  if (wonData.length > 0) {
    const wonHeaders = [
      'STT',
      'Ngày quay số',
      'Đài',
      'Dãy số',
      'Số lượng',
      'Giải trúng',
      'Giá trị giải thưởng (đ)',
    ];

    const wonRows = wonData.map((item, index) => {
      const region = item.ClassicLotteryChannel?.region || '';
      return [
        index + 1,
        item.ClassicResult?.resultDate || '',
        item.ClassicLotteryChannel?.name || '',
        formatNumbersWithSpace(item.ClassicLotteryStorage?.numbers || ''),
        item.amount || 0,
        parseRewardsForExcel(item.rewardNumbers, region),
        item.rewardAmount || 0,
      ];
    });

    const wsWon = XLSX.utils.aoa_to_sheet([wonHeaders, ...wonRows]);
    
    wsWon['!cols'] = [
      { wch: 6 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
      { wch: 10 },
      { wch: 35 },
      { wch: 22 },
    ];

    XLSX.utils.book_append_sheet(wb, wsWon, 'Vé trúng thưởng');
  }

  const channelStats = {};
  data.forEach(item => {
    const channelName = item.ClassicLotteryChannel?.name || 'Không xác định';
    if (!channelStats[channelName]) {
      channelStats[channelName] = {
        total: 0,
        totalAmount: 0,
        won: 0,
        wonAmount: 0,
        notWon: 0,
        pending: 0,
        rewardAmount: 0
      };
    }
    channelStats[channelName].total += 1;
    channelStats[channelName].totalAmount += item.amount || 0;
    channelStats[channelName].rewardAmount += Number(item.rewardAmount) || 0;
    
    if (item.status === 4) {
      channelStats[channelName].won += 1;
      channelStats[channelName].wonAmount += item.amount || 0;
    } else if (item.status === 5) {
      channelStats[channelName].notWon += 1;
    } else {
      channelStats[channelName].pending += 1;
    }
  });

  const channelHeaders = [
    'STT',
    'Đài',
    'Tổng vé',
    'Tổng SL',
    'Vé trúng',
    'SL trúng',
    'Vé không trúng',
    'Vé chờ',
    'Tổng tiền thưởng (đ)',
    'Tỷ lệ trúng'
  ];

  const channelRows = Object.entries(channelStats)
    .sort((a, b) => b[1].total - a[1].total)
    .map(([name, stats], index) => [
      index + 1,
      name,
      stats.total,
      stats.totalAmount,
      stats.won,
      stats.wonAmount,
      stats.notWon,
      stats.pending,
      stats.rewardAmount,
      stats.total > 0 ? ((stats.won / stats.total) * 100).toFixed(2) + '%' : '0%'
    ]);

  const wsChannel = XLSX.utils.aoa_to_sheet([channelHeaders, ...channelRows]);
  
  wsChannel['!cols'] = [
    { wch: 6 },
    { wch: 20 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 14 },
    { wch: 10 },
    { wch: 22 },
    { wch: 12 },
  ];

  XLSX.utils.book_append_sheet(wb, wsChannel, 'Thống kê theo đài');

  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const fileName = `Bao_cao_ve_e_${filter.fromDate || 'all'}_${filter.toDate || 'all'}_${dateStr}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

const MB_REWARDS = [
  { id: 'MB_GIAI_DB', name: 'Giải ĐB', order: 0 },
  { id: 'MB_GIAI_1', name: 'Giải Nhất', order: 1 },
  { id: 'MB_GIAI_2', name: 'Giải Nhì', order: 2 },
  { id: 'MB_GIAI_3', name: 'Giải Ba', order: 3 },
  { id: 'MB_GIAI_4', name: 'Giải Tư', order: 4 },
  { id: 'MB_GIAI_5', name: 'Giải Năm', order: 5 },
  { id: 'MB_GIAI_6', name: 'Giải Sáu', order: 6 },
  { id: 'MB_GIAI_7', name: 'Giải Bảy', order: 7 },
  { id: 'MB_GIAI_PHU_DB', name: 'Giải Phụ ĐB', order: 8 },
  { id: 'MB_GIAI_KK', name: 'Giải KK', order: 9 },
];

const MN_MT_REWARDS = [
  { id: 'MN_GIAI_DB', name: 'Giải ĐB', order: 0 },
  { id: 'MN_GIAI_1', name: 'Giải Nhất', order: 1 },
  { id: 'MN_GIAI_2', name: 'Giải Nhì', order: 2 },
  { id: 'MN_GIAI_3', name: 'Giải Ba', order: 3 },
  { id: 'MN_GIAI_4', name: 'Giải Tư', order: 4 },
  { id: 'MN_GIAI_5', name: 'Giải Năm', order: 5 },
  { id: 'MN_GIAI_6', name: 'Giải Sáu', order: 6 },
  { id: 'MN_GIAI_7', name: 'Giải Bảy', order: 7 },
  { id: 'MN_GIAI_8', name: 'Giải Tám', order: 8 },
  { id: 'MN_GIAI_PHU_DB', name: 'Giải Phụ ĐB', order: 9 },
  { id: 'MN_GIAI_KK', name: 'Giải KK', order: 10 },
  { id: 'MT_GIAI_DB', name: 'Giải ĐB', order: 0 },
  { id: 'MT_GIAI_1', name: 'Giải Nhất', order: 1 },
  { id: 'MT_GIAI_2', name: 'Giải Nhì', order: 2 },
  { id: 'MT_GIAI_3', name: 'Giải Ba', order: 3 },
  { id: 'MT_GIAI_4', name: 'Giải Tư', order: 4 },
  { id: 'MT_GIAI_5', name: 'Giải Năm', order: 5 },
  { id: 'MT_GIAI_6', name: 'Giải Sáu', order: 6 },
  { id: 'MT_GIAI_7', name: 'Giải Bảy', order: 7 },
  { id: 'MT_GIAI_8', name: 'Giải Tám', order: 8 },
  { id: 'MT_GIAI_PHU_DB', name: 'Giải Phụ ĐB', order: 9 },
  { id: 'MT_GIAI_KK', name: 'Giải KK', order: 10 },
];

const parseRewardsForExcel = (rewardNumbersJson, region) => {
  if (!rewardNumbersJson || rewardNumbersJson === '') {
    return '-';
  }

  try {
    const rewardNumbers = JSON.parse(rewardNumbersJson);
    const rewards = region === 'MB' ? MB_REWARDS : MN_MT_REWARDS;
    const result = [];

    Object.entries(rewardNumbers).forEach(([rewardId, count]) => {
      const reward = rewards.find((r) => r.id === rewardId);
      if (reward && count > 0) {
        result.push({ name: reward.name, count, order: reward.order });
      }
    });

    result.sort((a, b) => a.order - b.order);

    return result.length > 0 
      ? result.map(r => `${r.name} x${r.count}`).join(', ') 
      : '-';
  } catch {
    return '-';
  }
};

export const exportClassicRewardToExcel = (data, filter, region) => {
  const wb = XLSX.utils.book_new();

  const today = new Date().toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const regionText = region === 'MN' ? 'Miền Nam' : 'Miền Trung';

  const fromDateStr = filter.from ? new Date(filter.from).toLocaleDateString('vi-VN') : '';
  const toDateStr = filter.to ? new Date(filter.to).toLocaleDateString('vi-VN') : '';
  const timeRange = fromDateStr && toDateStr 
    ? `Từ ${fromDateStr} đến ${toDateStr}` 
    : fromDateStr || toDateStr || 'Tất cả';

  let totalGross = 0;
  let totalTax = 0;
  let totalFee = 0;
  let totalNet = 0;
  let paidCount = 0;
  let unpaidCount = 0;

  data.forEach(item => {
    totalGross += Number(item.grossValue) || 0;
    totalTax += Number(item.tax) || 0;
    totalFee += Number(item.fee) || 0;
    totalNet += Number(item.netValue) || 0;
    if (item.isPaid) {
      paidCount++;
    } else {
      unpaidCount++;
    }
  });

  const summaryData = [
    [`BÁO CÁO TRÚNG THƯỞNG - ${regionText.toUpperCase()}`],
    [],
    ['Ngày xuất báo cáo:', today],
    ['Thời gian lọc:', timeRange],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
    [],
    ['═══════════════════════════════════════════════════════'],
    ['THỐNG KÊ TỔNG QUAN'],
    ['═══════════════════════════════════════════════════════'],
    [],
    ['Tổng số vé trúng:', formatNumber(data.length)],
    ['Đã trả thưởng:', formatNumber(paidCount)],
    ['Chưa trả thưởng:', formatNumber(unpaidCount)],
    [],
    ['Tổng giải thưởng:', formatCurrency(totalGross)],
    ['Tổng thuế:', formatCurrency(totalTax)],
    ['Tổng phí:', formatCurrency(totalFee)],
    ['Tổng thực nhận:', formatCurrency(totalNet)],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  
  wsSummary['!cols'] = [
    { wch: 25 },
    { wch: 25 },
  ];
  
  wsSummary['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 6, c: 0 }, e: { r: 6, c: 1 } },
    { s: { r: 7, c: 0 }, e: { r: 7, c: 1 } },
    { s: { r: 8, c: 0 }, e: { r: 8, c: 1 } },
  ];

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Thống kê tổng quan');

  const headers = [
    'STT',
    'Ngày tạo',
    'Trạng thái',
    'Tên người chơi',
    'Số điện thoại',
    'Đài',
    'Ngày quay số',
    'Mã vé',
    'Số lượng',
    'Giải thưởng (đ)',
    'Thuế (đ)',
    'Phí (đ)',
    'Thực nhận (đ)',
  ];

  const dataRows = data.map((item, index) => {
    const createdAt = item.createdAt ? new Date(item.createdAt).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) : '';

    return [
      index + 1,
      createdAt,
      item.isPaid ? 'Đã trả' : 'Chưa trả',
      item.user?.name || '',
      item.user?.phone || '',
      item.ticket?.ClassicLotteryChannel?.name || '',
      item.ticket?.ClassicResult?.resultDate || '',
      item.ticket?.ClassicLotteryStorageId?.slice(-6) || '',
      item.ticket?.amount || 0,
      Number(item.grossValue) || 0,
      Number(item.tax) || 0,
      Number(item.fee) || 0,
      Number(item.netValue) || 0,
    ];
  });

  const wsData = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

  wsData['!cols'] = [
    { wch: 6 },
    { wch: 18 },
    { wch: 12 },
    { wch: 20 },
    { wch: 15 },
    { wch: 18 },
    { wch: 14 },
    { wch: 10 },
    { wch: 10 },
    { wch: 18 },
    { wch: 15 },
    { wch: 15 },
    { wch: 18 },
  ];

  XLSX.utils.book_append_sheet(wb, wsData, 'Chi tiết trúng thưởng');

  const unpaidData = data.filter(item => !item.isPaid);
  if (unpaidData.length > 0) {
    const unpaidHeaders = [
      'STT',
      'Tên người chơi',
      'Số điện thoại',
      'Đài',
      'Ngày quay số',
      'Giải thưởng (đ)',
      'Thực nhận (đ)',
    ];

    const unpaidRows = unpaidData.map((item, index) => [
      index + 1,
      item.user?.name || '',
      item.user?.phone || '',
      item.ticket?.ClassicLotteryChannel?.name || '',
      item.ticket?.ClassicResult?.resultDate || '',
      Number(item.grossValue) || 0,
      Number(item.netValue) || 0,
    ]);

    const wsUnpaid = XLSX.utils.aoa_to_sheet([unpaidHeaders, ...unpaidRows]);
    
    wsUnpaid['!cols'] = [
      { wch: 6 },
      { wch: 20 },
      { wch: 15 },
      { wch: 18 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
    ];

    XLSX.utils.book_append_sheet(wb, wsUnpaid, 'Chưa trả thưởng');
  }

  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const fileName = `Bao_cao_trung_thuong_${regionText.replace(' ', '_')}_${dateStr}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

export const exportLotoRewardToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  const today = formatDate(new Date());

  const fromDateStr = filter.from ? new Date(filter.from).toLocaleDateString('vi-VN') : '';
  const toDateStr = filter.to ? new Date(filter.to).toLocaleDateString('vi-VN') : '';
  const timeRange = fromDateStr && toDateStr
    ? `Từ ${fromDateStr} đến ${toDateStr}`
    : fromDateStr || toDateStr || 'Tất cả';

  let totalGross = 0;
  let totalTax = 0;
  let totalFee = 0;
  let totalNet = 0;
  let paidCount = 0;
  let unpaidCount = 0;

  data.forEach(item => {
    totalGross += Number(item.grossValue) || 0;
    totalTax += Number(item.tax) || 0;
    totalFee += Number(item.fee) || 0;
    totalNet += Number(item.netValue) || 0;
    if (item.isPaid) {
      paidCount++;
    } else {
      unpaidCount++;
    }
  });

  const summaryData = [
    ['BÁO CÁO TRÚNG THƯỞNG - LOTO'],
    [],
    ['Ngày xuất báo cáo:', today],
    ['Thời gian lọc:', timeRange],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
    [],
    ['═══════════════════════════════════════════════════════'],
    ['THỐNG KÊ TỔNG QUAN'],
    ['═══════════════════════════════════════════════════════'],
    [],
    ['Tổng số vé trúng:', formatNumber(data.length)],
    ['Đã trả thưởng:', formatNumber(paidCount)],
    ['Chưa trả thưởng:', formatNumber(unpaidCount)],
    [],
    ['Tổng giải thưởng:', formatCurrency(totalGross)],
    ['Tổng thuế:', formatCurrency(totalTax)],
    ['Tổng phí:', formatCurrency(totalFee)],
    ['Tổng thực nhận:', formatCurrency(totalNet)],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);

  wsSummary['!cols'] = [
    { wch: 25 },
    { wch: 25 },
  ];

  wsSummary['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 6, c: 0 }, e: { r: 6, c: 1 } },
    { s: { r: 7, c: 0 }, e: { r: 7, c: 1 } },
    { s: { r: 8, c: 0 }, e: { r: 8, c: 1 } },
  ];

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Thống kê tổng quan');

  const headers = [
    'STT',
    'Ngày trúng thưởng',
    'Trạng thái',
    'Nền tảng',
    'Tên người chơi',
    'Số điện thoại',
    'ID NV bán vé',
    'Tên NV bán vé',
    'Đài',
    'Ngày quay số',
    'Kiểu chơi',
    'Số dự thưởng',
    'Giá trị vé (đ)',
    'Giải thưởng (đ)',
    'Thuế (đ)',
    'Phí (đ)',
    'Thực nhận (đ)',
  ];

  const dataRows = data.map((item, index) => [
    index + 1,
    formatDate(item.createdAt),
    item.isPaid ? 'Đã trả' : 'Chưa trả',
    item.user?.endPointId || '',
    item.user?.name || '',
    item.user?.phone || '',
    item.ticket?.employee?.id ?? '',
    item.ticket?.employee?.name || '',
    item.ticket?.Channel?.name || '',
    item.ticket?.resultDate || '',
    getLotoTypeLabelWithFilter(item.ticket?.LotoType, item.ticket?.baoFilter, item.ticket?.baoDigits),
    item.ticket?.numbers || '',
    Number(item.ticket?.value) || 0,
    Number(item.grossValue) || 0,
    Number(item.tax) || 0,
    Number(item.fee) || 0,
    Number(item.netValue) || 0,
  ]);

  const wsData = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

  wsData['!cols'] = [
    { wch: 6 },
    { wch: 18 },
    { wch: 12 },
    { wch: 12 },
    { wch: 20 },
    { wch: 15 },
    { wch: 12 },
    { wch: 20 },
    { wch: 18 },
    { wch: 14 },
    { wch: 20 },
    { wch: 18 },
    { wch: 15 },
    { wch: 18 },
    { wch: 15 },
    { wch: 15 },
    { wch: 18 },
  ];

  XLSX.utils.book_append_sheet(wb, wsData, 'Chi tiết trúng thưởng');

  const unpaidData = data.filter(item => !item.isPaid);
  if (unpaidData.length > 0) {
    const unpaidHeaders = [
      'STT',
      'Tên người chơi',
      'Số điện thoại',
      'ID NV bán vé',
      'Tên NV bán vé',
      'Đài',
      'Ngày quay số',
      'Kiểu chơi',
      'Số dự thưởng',
      'Giải thưởng (đ)',
      'Thực nhận (đ)',
    ];

    const unpaidRows = unpaidData.map((item, index) => [
      index + 1,
      item.user?.name || '',
      item.user?.phone || '',
      item.ticket?.employee?.id ?? '',
      item.ticket?.employee?.name || '',
      item.ticket?.Channel?.name || '',
      item.ticket?.resultDate || '',
      getLotoTypeLabelWithFilter(item.ticket?.LotoType, item.ticket?.baoFilter, item.ticket?.baoDigits),
      item.ticket?.numbers || '',
      Number(item.grossValue) || 0,
      Number(item.netValue) || 0,
    ]);

    const wsUnpaid = XLSX.utils.aoa_to_sheet([unpaidHeaders, ...unpaidRows]);

    wsUnpaid['!cols'] = [
      { wch: 6 },
      { wch: 20 },
      { wch: 15 },
      { wch: 12 },
      { wch: 20 },
      { wch: 18 },
      { wch: 14 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];

    XLSX.utils.book_append_sheet(wb, wsUnpaid, 'Chưa trả thưởng');
  }

  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  XLSX.writeFile(wb, `Bao_cao_trung_thuong_Loto_${dateStr}.xlsx`);
};

export const exportClassicOrderToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  const today = new Date().toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const fromDateStr = filter.from ? new Date(filter.from).toLocaleDateString('vi-VN') : '';
  const toDateStr = filter.to ? new Date(filter.to).toLocaleDateString('vi-VN') : '';
  const timeRange = fromDateStr && toDateStr 
    ? `Từ ${fromDateStr} đến ${toDateStr}` 
    : fromDateStr || toDateStr || 'Tất cả';

  const dialFromStr = filter.dialFrom ? new Date(filter.dialFrom).toLocaleDateString('vi-VN') : '';
  const dialToStr = filter.dialTo ? new Date(filter.dialTo).toLocaleDateString('vi-VN') : '';
  const dialRange = dialFromStr && dialToStr
    ? `Từ ${dialFromStr} đến ${dialToStr}`
    : dialFromStr || dialToStr || 'Tất cả';

  let totalOrders = data.length;
  let totalValue = 0;
  let totalTickets = 0;
  let newOrders = 0;
  let doneOrders = 0;

  data.forEach(item => {
    totalValue += Number(item.totalValue) || 0;
    const ticketCount = item.tickets?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
    totalTickets += ticketCount;
    if ([1, 2].includes(item.status)) {
      newOrders++;
    } else if ([3, 4, 5].includes(item.status)) {
      doneOrders++;
    }
  });

  const summaryData = [
    ['BÁO CÁO ĐƠN HÀNG - XỔ SỐ TRUYỀN THỐNG'],
    [],
    ['Ngày xuất báo cáo:', today],
    ['Thời gian lọc:', timeRange],
    ['Kỳ xổ:', dialRange],
    ['Vùng miền:', getRegionText(filter.region) || 'Tất cả'],
    [],
    ['═══════════════════════════════════════════════════════'],
    ['THỐNG KÊ TỔNG QUAN'],
    ['═══════════════════════════════════════════════════════'],
    [],
    ['Tổng số đơn hàng:', formatNumber(totalOrders)],
    ['Đơn mới:', formatNumber(newOrders)],
    ['Đơn hoàn thành:', formatNumber(doneOrders)],
    [],
    ['Tổng giá trị:', formatCurrency(totalValue)],
    ['Tổng số vé:', formatNumber(totalTickets)],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  
  wsSummary['!cols'] = [
    { wch: 25 },
    { wch: 25 },
  ];
  
  wsSummary['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 6, c: 0 }, e: { r: 6, c: 1 } },
    { s: { r: 7, c: 0 }, e: { r: 7, c: 1 } },
    { s: { r: 8, c: 0 }, e: { r: 8, c: 1 } },
  ];

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Thống kê tổng quan');

  const headers = [
    'STT',
    'Mã đơn',
    'Ngày tạo',
    'Ngày hoàn thành',
    'Trạng thái',
    'Vùng miền',
    'Tên khách hàng',
    'Số điện thoại',
    'Số lượng vé',
    'Giá trị đơn (đ)',
    'Vé trúng',
    'Vé không trúng',
    'Vé chờ KQ',
    'Tiền trúng (đ)',
  ];

  const dataRows = data.map((item, index) => {
    const ticketCount = item.tickets?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
    
    let wonAmount = 0;
    let notWonAmount = 0;
    let pendingAmount = 0;
    let totalReward = 0;

    (item.tickets || []).forEach(t => {
      const amount = t.amount || 0;
      if (t.status === 4) {
        wonAmount += amount;
        totalReward += Number(t.reward || t.rewardAmount || 0);
      } else if (t.status === 5) {
        notWonAmount += amount;
      } else {
        pendingAmount += amount;
      }
    });

    const statusText = [1, 2].includes(item.status) ? 'Đơn mới' : 
                       [3, 4, 5].includes(item.status) ? 'Hoàn thành' : 'Không xác định';

    return [
      index + 1,
      item.id || '',
      formatDate(item.createdAt),
      formatDate(item.doneAt),
      statusText,
      getRegionText(item.region),
      item.user?.name || '',
      item.user?.phone || '',
      ticketCount,
      Number(item.totalValue) || 0,
      wonAmount,
      notWonAmount,
      pendingAmount,
      totalReward,
    ];
  });

  const wsData = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

  wsData['!cols'] = [
    { wch: 6 },
    { wch: 28 },
    { wch: 18 },
    { wch: 18 },
    { wch: 14 },
    { wch: 12 },
    { wch: 20 },
    { wch: 15 },
    { wch: 12 },
    { wch: 18 },
    { wch: 10 },
    { wch: 14 },
    { wch: 12 },
    { wch: 18 },
  ];

  XLSX.utils.book_append_sheet(wb, wsData, 'Chi tiết đơn hàng');

  const ticketHeaders = [
    'STT',
    'Mã đơn',
    'Đài',
    'Số lượng',
    'Trạng thái vé',
    'Tiền trúng (đ)',
    'Tên khách hàng',
    'Số điện thoại',
  ];

  const ticketRows = [];
  let ticketIndex = 0;

  data.forEach(order => {
    (order.tickets || []).forEach(ticket => {
      ticketIndex++;
      const statusText = ticket.status === 4 ? 'Trúng thưởng' : 
                         ticket.status === 5 ? 'Không trúng' : 'Chờ kết quả';
      ticketRows.push([
        ticketIndex,
        order.id || '',
        ticket.channelName || '',
        ticket.amount || 0,
        statusText,
        Number(ticket.reward || ticket.rewardAmount || 0),
        order.user?.name || '',
        order.user?.phone || '',
      ]);
    });
  });

  if (ticketRows.length > 0) {
    const wsTickets = XLSX.utils.aoa_to_sheet([ticketHeaders, ...ticketRows]);
    
    wsTickets['!cols'] = [
      { wch: 6 },
      { wch: 28 },
      { wch: 20 },
      { wch: 10 },
      { wch: 15 },
      { wch: 18 },
      { wch: 20 },
      { wch: 15 },
    ];

    XLSX.utils.book_append_sheet(wb, wsTickets, 'Chi tiết vé');
  }

  const dateStrFile = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const fileName = `Bao_cao_don_hang_${dateStrFile}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

const RETURN_REASONS = {
  BAN_E: 'Bán ế',
  HET_VE: 'Hết vé',
  VE_HU: 'Vé hư',
  LY_DO_KHAC: 'Lý do khác',
};

export const exportTicketReturnToExcel = (data, summary, filter) => {
  const wb = XLSX.utils.book_new();

  const today = new Date().toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const fromDateStr = filter.from ? new Date(filter.from).toLocaleDateString('vi-VN') : '';
  const toDateStr = filter.to ? new Date(filter.to).toLocaleDateString('vi-VN') : '';
  const timeRange = fromDateStr && toDateStr 
    ? `Từ ${fromDateStr} đến ${toDateStr}` 
    : fromDateStr 
      ? `Từ ${fromDateStr}` 
      : toDateStr 
        ? `Đến ${toDateStr}` 
        : 'Tất cả thời gian';

  const regionText = filter.region ? getRegionText(filter.region) : 'Tất cả';
  const reasonText = filter.reason && RETURN_REASONS[filter.reason] 
    ? RETURN_REASONS[filter.reason] 
    : 'Tất cả lý do';

  // Sheet 1: Thống kê tổng quan
  const summaryData = [
    ['BÁO CÁO HOÀN VÉ - XỔ SỐ TRUYỀN THỐNG'],
    [],
    ['Ngày xuất báo cáo:', today],
    ['Thời gian lọc:', timeRange],
    ['Vùng miền:', regionText],
    ['Lý do:', reasonText],
    [],
    ['═══════════════════════════════════════════════════════'],
    ['THỐNG KÊ TỔNG QUAN'],
    ['═══════════════════════════════════════════════════════'],
    [],
    ['Tổng số lần hoàn:', formatNumber(summary?.totalCount || 0)],
    ['Tổng số vé hoàn:', formatNumber(summary?.totalAmount || 0)],
    [],
    ['═══════════════════════════════════════════════════════'],
    ['THỐNG KÊ THEO LÝ DO'],
    ['═══════════════════════════════════════════════════════'],
    [],
  ];

  // Add reason breakdown
  if (summary?.byReason && summary.byReason.length > 0) {
    summaryData.push(['Lý do', 'Số lần hoàn', 'Số vé hoàn']);
    summary.byReason.forEach(r => {
      summaryData.push([
        r.reasonText || RETURN_REASONS[r.reason] || r.reason,
        formatNumber(r.count || 0),
        formatNumber(r.totalAmount || 0)
      ]);
    });
  }

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  
  wsSummary['!cols'] = [
    { wch: 30 },
    { wch: 20 },
    { wch: 20 },
  ];
  
  wsSummary['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
    { s: { r: 7, c: 0 }, e: { r: 7, c: 2 } },
    { s: { r: 8, c: 0 }, e: { r: 8, c: 2 } },
    { s: { r: 9, c: 0 }, e: { r: 9, c: 2 } },
    { s: { r: 14, c: 0 }, e: { r: 14, c: 2 } },
    { s: { r: 15, c: 0 }, e: { r: 15, c: 2 } },
    { s: { r: 16, c: 0 }, e: { r: 16, c: 2 } },
  ];

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Thống kê tổng quan');

  // Sheet 2: Chi tiết hoàn vé
  const headers = [
    'STT',
    'Mã vé',
    'Số vé',
    'Đài',
    'Vùng',
    'Ngày xổ',
    'SL nhập',
    'SL hoàn',
    'SL còn',
    'Lý do',
    'Chi tiết lý do',
    'Thời gian hoàn',
    'Người hoàn',
  ];

  const dataRows = data.map((item, index) => {
    const storage = item.storage || {};
    const channel = storage.ClassicLotteryChannel || {};
    const result = storage.ClassicResult || {};
    
    return [
      index + 1,
      storage.id || '',
      storage.numbers || '',
      channel.name || '',
      getRegionText(storage.region),
      result.resultDate || '',
      storage.inputAmount || 0,
      item.returnAmount || 0,
      storage.amount || 0,
      RETURN_REASONS[item.reason] || item.reasonText || item.reason || '',
      item.reasonDetail || '',
      formatDate(item.createdAt),
      item.returnedByName || item.returnedByAdmin?.name || item.returnedByAdmin?.email || '',
    ];
  });

  const wsData = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);

  wsData['!cols'] = [
    { wch: 6 },   // STT
    { wch: 36 },  // Mã vé
    { wch: 12 },  // Số vé
    { wch: 20 },  // Đài
    { wch: 12 },  // Vùng
    { wch: 12 },  // Ngày xổ
    { wch: 10 },  // SL nhập
    { wch: 10 },  // SL hoàn
    { wch: 10 },  // SL còn
    { wch: 15 },  // Lý do
    { wch: 25 },  // Chi tiết lý do
    { wch: 20 },  // Thời gian hoàn
    { wch: 18 },  // Người hoàn
  ];

  XLSX.utils.book_append_sheet(wb, wsData, 'Chi tiết hoàn vé');

  // Sheet 3: Thống kê theo đài
  const channelStats = {};
  data.forEach(item => {
    const storage = item.storage || {};
    const channel = storage.ClassicLotteryChannel || {};
    const channelName = channel.name || 'Không xác định';
    
    if (!channelStats[channelName]) {
      channelStats[channelName] = {
        count: 0,
        totalAmount: 0,
        byReason: {}
      };
    }
    channelStats[channelName].count += 1;
    channelStats[channelName].totalAmount += item.returnAmount || 0;
    
    const reason = item.reason || 'UNKNOWN';
    if (!channelStats[channelName].byReason[reason]) {
      channelStats[channelName].byReason[reason] = 0;
    }
    channelStats[channelName].byReason[reason] += item.returnAmount || 0;
  });

  const channelHeaders = [
    'STT',
    'Đài',
    'Số lần hoàn',
    'Tổng vé hoàn',
    'Bán ế',
    'Hết vé',
    'Vé hư',
    'Lý do khác',
  ];

  const channelRows = Object.entries(channelStats)
    .sort((a, b) => b[1].totalAmount - a[1].totalAmount)
    .map(([name, stats], index) => [
      index + 1,
      name,
      stats.count,
      stats.totalAmount,
      stats.byReason.BAN_E || 0,
      stats.byReason.HET_VE || 0,
      stats.byReason.VE_HU || 0,
      stats.byReason.LY_DO_KHAC || 0,
    ]);

  const wsChannel = XLSX.utils.aoa_to_sheet([channelHeaders, ...channelRows]);
  
  wsChannel['!cols'] = [
    { wch: 6 },
    { wch: 25 },
    { wch: 14 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
  ];

  XLSX.utils.book_append_sheet(wb, wsChannel, 'Thống kê theo đài');

  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const fileName = `Bao_cao_hoan_ve_${dateStr}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

export const exportSalesTicketStorageToExcel = (data, summary, filter) => {
  const wb = XLSX.utils.book_new();

  const today = new Date().toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const fromDateStr = filter.fromDate ? new Date(filter.fromDate).toLocaleDateString('vi-VN') : '';
  const toDateStr = filter.toDate ? new Date(filter.toDate).toLocaleDateString('vi-VN') : '';
  const timeRange = fromDateStr && toDateStr
    ? `Từ ${fromDateStr} đến ${toDateStr}`
    : fromDateStr
      ? `Từ ${fromDateStr}`
      : toDateStr
        ? `Đến ${toDateStr}`
        : 'Tất cả thời gian';

  const getSummaryValue = (key) => {
    const item = (summary || []).find((it) => it.key === key);
    return item ? item.sum : 0;
  };

  const summaryRows = [
    ['BÁO CÁO QUẢN LÍ VÉ - XỔ SỐ TRUYỀN THỐNG'],
    [],
    ['Ngày xuất báo cáo:', today],
    ['Ngày xổ:', timeRange],
    ['Vùng miền:', getRegionText(filter.region)],
    ['Đài:', filter.channelName || 'Tất cả đài'],
    ['Loại vé:', filter.ticketType === 'single' ? 'Vé đơn' : filter.ticketType === 'pair' ? 'Cặp nguyên' : 'Tất cả loại vé'],
    ['Dãy số:', filter.numbers || 'Tất cả'],
    [],
    ['═══════════════════════════════════════════════════════'],
    ['THỐNG KÊ TỔNG QUAN'],
    ['═══════════════════════════════════════════════════════'],
    [],
    ['Tổng vé nhập:', formatNumber(getSummaryValue('input'))],
    ['Tổng vé bán:', formatNumber(getSummaryValue('sold'))],
    ['Tổng vé hoàn trả:', formatNumber(getSummaryValue('returned'))],
    ['Tổng vé tồn:', formatNumber(getSummaryValue('remaining'))],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);

  wsSummary['!cols'] = [{ wch: 30 }, { wch: 25 }];

  wsSummary['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
    { s: { r: 9, c: 0 }, e: { r: 9, c: 1 } },
    { s: { r: 10, c: 0 }, e: { r: 10, c: 1 } },
    { s: { r: 11, c: 0 }, e: { r: 11, c: 1 } },
  ];

  XLSX.utils.book_append_sheet(wb, wsSummary, 'Thống kê tổng quan');

  const headers = [
    'STT',
    'Mã vé',
    'Loại vé',
    'Đài',
    'Ngày xổ',
    'Bộ số',
    'SL nhập',
    'SL bán',
    'SL tồn',
    'SL hoàn',
    'Trạng thái bán',
    'Trạng thái hoàn',
    'Ngày tạo',
    'Người nhập',
    'Người duyệt',
  ];

  const totals = data.reduce(
    (acc, item) => ({
      inputAmount: acc.inputAmount + (item.inputAmount || 0),
      soldAmount: acc.soldAmount + (item.soldAmount || 0),
      amount: acc.amount + (item.amount || 0),
      returnedAmount: acc.returnedAmount + (item.returnedAmount || 0),
    }),
    { inputAmount: 0, soldAmount: 0, amount: 0, returnedAmount: 0 },
  );

  const dataRows = data.map((item, index) => [
    index + 1,
    item.id || '',
    item.isSingle === false ? 'Cặp nguyên' : 'Vé đơn',
    item.channelName || '',
    item.resultDate || '',
    formatNumbersWithSpace(item.numbers),
    item.inputAmount || 0,
    item.soldAmount || 0,
    item.amount || 0,
    item.returnedAmount || 0,
    item.saleStatusText || '',
    item.returnStatusText || '',
    formatDate(item.createdAt),
    item.createdByName || '',
    item.approvedByName || '',
  ]);

  const totalRow = [
    '',
    'TỔNG CỘNG',
    '',
    '',
    '',
    '',
    totals.inputAmount,
    totals.soldAmount,
    totals.amount,
    totals.returnedAmount,
    '',
    '',
    '',
    '',
    '',
  ];

  const wsData = XLSX.utils.aoa_to_sheet([headers, ...dataRows, totalRow]);

  wsData['!cols'] = [
    { wch: 6 },   // STT
    { wch: 28 },  // Mã vé
    { wch: 14 },  // Loại vé
    { wch: 20 },  // Đài
    { wch: 12 },  // Ngày xổ
    { wch: 18 },  // Bộ số
    { wch: 10 },  // SL nhập
    { wch: 10 },  // SL bán
    { wch: 10 },  // SL tồn
    { wch: 10 },  // SL hoàn
    { wch: 16 },  // Trạng thái bán
    { wch: 16 },  // Trạng thái hoàn
    { wch: 18 },  // Ngày tạo
    { wch: 20 },  // Người nhập
    { wch: 20 },  // Người duyệt
  ];

  XLSX.utils.book_append_sheet(wb, wsData, 'Chi tiết vé');

  const channelStats = {};
  data.forEach((item) => {
    const channelName = item.channelName || 'Không xác định';

    if (!channelStats[channelName]) {
      channelStats[channelName] = {
        inputAmount: 0,
        soldAmount: 0,
        amount: 0,
        returnedAmount: 0,
      };
    }

    channelStats[channelName].inputAmount += item.inputAmount || 0;
    channelStats[channelName].soldAmount += item.soldAmount || 0;
    channelStats[channelName].amount += item.amount || 0;
    channelStats[channelName].returnedAmount += item.returnedAmount || 0;
  });

  const channelHeaders = ['STT', 'Đài', 'SL nhập', 'SL bán', 'SL tồn', 'SL hoàn'];

  const channelRows = Object.entries(channelStats)
    .sort((a, b) => b[1].inputAmount - a[1].inputAmount)
    .map(([name, stats], index) => [
      index + 1,
      name,
      stats.inputAmount,
      stats.soldAmount,
      stats.amount,
      stats.returnedAmount,
    ]);

  const wsChannel = XLSX.utils.aoa_to_sheet([channelHeaders, ...channelRows]);

  wsChannel['!cols'] = [
    { wch: 6 },
    { wch: 25 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
  ];

  XLSX.utils.book_append_sheet(wb, wsChannel, 'Thống kê theo đài');

  const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const fileName = `Bao_cao_quan_li_ve_${dateStr}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

export const exportLotoTicketStatisticToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  let totalValue = 0;
  let totalRewardValue = 0;
  let rewardedCount = 0;

  data.forEach((item) => {
    totalValue += Number(item.value) || 0;
    totalRewardValue += Number(item.rewardValue) || 0;
    if (item.isRewarded) rewardedCount++;
  });

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
  ];

  const stats = [
    ['Tổng số vé:', formatNumber(data.length)],
    ['Trúng thưởng:', formatNumber(rewardedCount)],
    ['Không trúng:', formatNumber(data.length - rewardedCount)],
    [],
    ['Tổng giá trị vé:', formatCurrency(totalValue)],
    ['Tổng tiền thưởng:', formatCurrency(totalRewardValue)],
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet('THỐNG KÊ VÉ LOTO THEO KỲ', infoRows, stats), 'Thống kê tổng quan');

  const columns = [
    { label: 'STT', width: 6 },
    { label: 'Mã đơn', width: 22 },
    { label: 'Mã vé', width: 22 },
    { label: 'Tên khách hàng', width: 20 },
    { label: 'Số điện thoại', width: 15 },
    { label: 'Kiểu chơi', width: 20 },
    { label: 'Dãy số', width: 18 },
    { label: 'Giá trị (đ)', width: 15 },
    { label: 'Trạng thái', width: 15 },
    { label: 'Số lần trúng', width: 12 },
    { label: 'Đơn giá thưởng (đ)', width: 18 },
    { label: 'Tiền thưởng (đ)', width: 18 },
    { label: 'Ngày xuất', width: 18 },
  ];

  const rows = data.map((item, index) => [
    index + 1,
    item.cartId || '',
    item.id || '',
    item.name || '',
    item.phone || '',
    getLotoTypeLabelWithFilter(item.lotoType, item.baoFilter, item.baoDigits),
    item.numbers || '',
    Number(item.value) || 0,
    item.isRewarded ? 'Trúng thưởng' : 'Không trúng',
    Number(item.rewardCount) || 0,
    Number(item.rewardUnitValue) || 0,
    Number(item.rewardValue) || 0,
    formatDate(item.confirmAt),
  ]);

  XLSX.utils.book_append_sheet(wb, buildDataSheet(columns, rows), 'Chi tiết vé');

  writeWorkbook(wb, 'Thong_ke_ve_Loto');
};

export const exportClassicTicketStatisticToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  let totalAmount = 0;
  let unmarketableCount = 0;
  let rewardedCount = 0;
  let unrewardedCount = 0;

  data.forEach((item) => {
    totalAmount += Number(item.amount) || 0;
    if (item.isUnmarketable) unmarketableCount++;
    if (item.status === 4) rewardedCount++;
    if (item.status === 5) unrewardedCount++;
  });

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
  ];

  const stats = [
    ['Tổng số vé:', formatNumber(data.length)],
    ['Vé bán:', formatNumber(data.length - unmarketableCount)],
    ['Vé ế:', formatNumber(unmarketableCount)],
    [],
    ['Trúng thưởng:', formatNumber(rewardedCount)],
    ['Không trúng:', formatNumber(unrewardedCount)],
    ['Chưa tính giải:', formatNumber(data.length - rewardedCount - unrewardedCount)],
    [],
    ['Tổng số lượng vé:', formatNumber(totalAmount)],
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet('THỐNG KÊ VÉ KIẾN THIẾT THEO KỲ', infoRows, stats), 'Thống kê tổng quan');

  const columns = [
    { label: 'STT', width: 6 },
    { label: 'Mã đơn', width: 22 },
    { label: 'Mã vé', width: 22 },
    { label: 'Tên khách hàng', width: 20 },
    { label: 'Số điện thoại', width: 15 },
    { label: 'Dãy số', width: 18 },
    { label: 'Số lượng', width: 10 },
    { label: 'Trạng thái', width: 16 },
    { label: 'Loại vé', width: 12 },
    { label: 'Ngày xuất', width: 18 },
  ];

  const rows = data.map((item, index) => [
    index + 1,
    item.cartId || '',
    item.id || '',
    item.name || '',
    item.phone || '',
    item.numbers || '',
    Number(item.amount) || 0,
    getWinningStatusText(item.status),
    item.isUnmarketable ? 'Vé ế' : 'Vé bán',
    formatDate(item.confirmAt),
  ]);

  XLSX.utils.book_append_sheet(wb, buildDataSheet(columns, rows), 'Chi tiết vé');

  writeWorkbook(wb, 'Thong_ke_ve_kien_thiet');
};

export const exportClassicStatisticToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  let totalAmount = 0;
  const amountByChannel = new Map();

  data.forEach((item) => {
    const amount = Number(item.amount) || 0;
    totalAmount += amount;
    const channel = item.channelName || '';
    amountByChannel.set(channel, (amountByChannel.get(channel) || 0) + amount);
  });

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Thời gian lọc:', formatFilterRange(filter.from, filter.to)],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
  ];

  const stats = [
    ['Tổng số vé:', formatNumber(data.length)],
    ['Tổng số lượng vé:', formatNumber(totalAmount)],
    [],
    ['SỐ LƯỢNG THEO ĐÀI'],
    ...Array.from(amountByChannel.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([channel, amount]) => [channel, formatNumber(amount)]),
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet('THỐNG KÊ VÉ KIẾN THIẾT ĐÃ XUẤT', infoRows, stats), 'Thống kê tổng quan');

  const columns = [
    { label: 'STT', width: 6 },
    { label: 'Mã đơn', width: 22 },
    { label: 'Mã vé', width: 22 },
    { label: 'Tên khách hàng', width: 20 },
    { label: 'Số điện thoại', width: 15 },
    { label: 'Dãy số', width: 18 },
    { label: 'Số lượng', width: 10 },
    { label: 'Đài', width: 18 },
    { label: 'Ngày quay số', width: 14 },
    { label: 'Ngày mua', width: 18 },
    { label: 'Ngày xuất', width: 18 },
    { label: 'Trạng thái', width: 16 },
  ];

  const rows = data.map((item, index) => [
    index + 1,
    item.cartId || '',
    item.id || '',
    item.name || '',
    item.phone || '',
    item.numbers || '',
    Number(item.amount) || 0,
    item.channelName || '',
    item.resultDate || '',
    formatDate(item.createdAt),
    formatDate(item.confirmAt),
    getWinningStatusText(item.status),
  ]);

  XLSX.utils.book_append_sheet(wb, buildDataSheet(columns, rows), 'Chi tiết vé');

  writeWorkbook(wb, 'Thong_ke_kien_thiet');
};

export const exportChargeStatisticToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  let totalValue = 0;
  let totalFee = 0;
  const valueByBank = new Map();

  data.forEach((item) => {
    const value = Number(item.value) || 0;
    totalValue += value;
    totalFee += Number(item.fee) || 0;
    const bank = item.bank || '';
    valueByBank.set(bank, (valueByBank.get(bank) || 0) + value);
  });

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Thời gian lọc:', formatFilterRange(filter.from, filter.to)],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
  ];

  const stats = [
    ['Tổng số giao dịch:', formatNumber(data.length)],
    ['Tổng tiền nạp:', formatCurrency(totalValue)],
    ['Tổng phí:', formatCurrency(totalFee)],
    [],
    ['SỐ TIỀN THEO NGÂN HÀNG'],
    ...Array.from(valueByBank.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([bank, value]) => [bank, formatCurrency(value)]),
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet('THỐNG KÊ NẠP TIỀN THEO NGÂN HÀNG', infoRows, stats), 'Thống kê tổng quan');

  const columns = [
    { label: 'STT', width: 6 },
    { label: 'Tên khách hàng', width: 22 },
    { label: 'Số điện thoại', width: 15 },
    { label: 'Ngân hàng', width: 15 },
    { label: 'Số tiền (đ)', width: 16 },
    { label: 'Phí (đ)', width: 14 },
    { label: 'Mã đối chiếu', width: 24 },
    { label: 'Ngày', width: 18 },
  ];

  const rows = data.map((item, index) => [
    index + 1,
    item.name || '',
    item.phone || '',
    item.bank || '',
    Number(item.value) || 0,
    Number(item.fee) || 0,
    item.referenceNumber || '',
    formatDate(item.createdAt),
  ]);

  XLSX.utils.book_append_sheet(wb, buildDataSheet(columns, rows), 'Chi tiết giao dịch');

  writeWorkbook(wb, 'Thong_ke_nap_tien');
};

export const exportWithdrawStatisticToExcel = (data, filter, sourceLabel) => {
  const wb = XLSX.utils.book_new();

  let totalValue = 0;
  const valueByMethod = new Map();

  data.forEach((item) => {
    const value = Number(item.value) || 0;
    totalValue += value;
    const method = item.bank || '';
    valueByMethod.set(method, (valueByMethod.get(method) || 0) + value);
  });

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Nguồn:', sourceLabel],
    ['Thời gian lọc:', formatFilterRange(filter.from, filter.to)],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
  ];

  const stats = [
    ['Tổng số lệnh rút:', formatNumber(data.length)],
    ['Tổng tiền rút:', formatCurrency(totalValue)],
    [],
    ['SỐ TIỀN THEO HÌNH THỨC'],
    ...Array.from(valueByMethod.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([method, value]) => [method, formatCurrency(value)]),
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet(`THỐNG KÊ RÚT TIỀN - ${sourceLabel.toUpperCase()}`, infoRows, stats), 'Thống kê tổng quan');

  const columns = [
    { label: 'STT', width: 6 },
    { label: 'Tên khách hàng', width: 22 },
    { label: 'Số điện thoại', width: 15 },
    { label: 'Số tiền (đ)', width: 16 },
    { label: 'Hình thức', width: 18 },
    { label: 'Ngày', width: 18 },
  ];

  const rows = data.map((item, index) => [
    index + 1,
    item.name || '',
    item.phone || '',
    Number(item.value) || 0,
    item.bank || '',
    formatDate(item.createdAt),
  ]);

  XLSX.utils.book_append_sheet(wb, buildDataSheet(columns, rows), 'Chi tiết lệnh rút');

  writeWorkbook(wb, 'Thong_ke_rut_tien');
};

export const exportEwalletAndPaymentGatewayStatisticToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  let totalValue = 0;
  let totalFee = 0;
  let totalAmount = 0;

  data.forEach((item) => {
    totalValue += Number(item.value) || 0;
    totalFee += Number(item.fee) || 0;
    totalAmount += Number(item.total) || 0;
  });

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Thời gian lọc:', formatFilterRange(filter.from, filter.to)],
    ['Phương thức:', filter.method || 'Tất cả'],
    ['Kênh:', filter.subMethod || 'Tất cả'],
  ];

  const stats = [
    ['Tổng số giao dịch:', formatNumber(data.length)],
    ['Tổng giá trị:', formatCurrency(totalValue)],
    ['Tổng phí:', formatCurrency(totalFee)],
    ['Tổng cộng:', formatCurrency(totalAmount)],
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet('THỐNG KÊ VÍ & CỔNG THANH TOÁN', infoRows, stats), 'Thống kê tổng quan');

  const columns = [
    { label: 'STT', width: 6 },
    { label: 'Mã giao dịch', width: 26 },
    { label: 'Mã GD đối tác', width: 26 },
    { label: 'Giá trị (đ)', width: 16 },
    { label: 'Phí (đ)', width: 14 },
    { label: 'Tổng (đ)', width: 16 },
    { label: 'Ngày giao dịch', width: 18 },
  ];

  const rows = data.map((item, index) => [
    index + 1,
    item.transid || '',
    item.refid || '',
    Number(item.value) || 0,
    Number(item.fee) || 0,
    Number(item.total) || 0,
    formatDate(item.date),
  ]);

  XLSX.utils.book_append_sheet(wb, buildDataSheet(columns, rows), 'Chi tiết giao dịch');

  writeWorkbook(wb, 'Thong_ke_vi_va_cong_thanh_toan');
};

export const exportRefundHistoryToExcel = (data, filter, sourceLabel) => {
  const wb = XLSX.utils.book_new();

  let totalValue = 0;

  data.forEach((item) => {
    totalValue += Number(item.value) || 0;
  });

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Nguồn:', sourceLabel],
    ['Thời gian lọc:', formatFilterRange(filter.from, filter.to)],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
  ];

  const stats = [
    ['Tổng số giao dịch:', formatNumber(data.length)],
    ['Tổng tiền hoàn:', formatCurrency(totalValue)],
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet(`LỊCH SỬ HOÀN TIỀN - ${sourceLabel.toUpperCase()}`, infoRows, stats), 'Thống kê tổng quan');

  const columns = [
    { label: 'STT', width: 6 },
    { label: 'Mã giao dịch', width: 26 },
    { label: 'Tên khách hàng', width: 22 },
    { label: 'Số điện thoại', width: 15 },
    { label: 'Số tiền (đ)', width: 16 },
    { label: 'Lý do', width: 40 },
    { label: 'Ngày hoàn', width: 18 },
  ];

  const rows = data.map((item, index) => [
    index + 1,
    item.id || '',
    item.name || '',
    item.phone || '',
    Number(item.value) || 0,
    item.reason || '',
    formatDate(item.createdAt),
  ]);

  XLSX.utils.book_append_sheet(wb, buildDataSheet(columns, rows), 'Chi tiết giao dịch');

  writeWorkbook(wb, 'Lich_su_hoan_tien');
};

export const exportTransactionHistoryToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  let credit = 0;
  let debit = 0;
  const netByWallet = new Map();

  data.forEach((item) => {
    const value = Number(item.commitedAmount) || 0;
    if (value >= 0) {
      credit += value;
    } else {
      debit += value;
    }
    const wallet = getWalletLabel(item.wallet);
    netByWallet.set(wallet, (netByWallet.get(wallet) || 0) + value);
  });

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Thời gian lọc:', formatFilterRange(filter.from, filter.to)],
    ['Số điện thoại:', filter.phone || 'Tất cả'],
    ['Loại ví:', filter.wallet ? getWalletLabel(filter.wallet) : 'Tất cả'],
  ];

  const stats = [
    ['Tổng số bút toán:', formatNumber(data.length)],
    ['Tổng ghi có:', formatCurrency(credit)],
    ['Tổng ghi nợ:', formatCurrency(debit)],
    ['Chênh lệch ròng:', formatCurrency(credit + debit)],
    [],
    ['CHÊNH LỆCH RÒNG THEO VÍ'],
    ...Array.from(netByWallet.entries()).map(([wallet, value]) => [wallet, formatCurrency(value)]),
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet('LỊCH SỬ GIAO DỊCH VÍ KHÁCH HÀNG', infoRows, stats), 'Thống kê tổng quan');

  const columns = [
    { label: 'STT', width: 6 },
    { label: 'Thời gian', width: 18 },
    { label: 'Tên khách hàng', width: 22 },
    { label: 'Số điện thoại', width: 15 },
    { label: 'Ví', width: 12 },
    { label: 'Số tiền (đ)', width: 16 },
    { label: 'Nội dung', width: 40 },
  ];

  const rows = data.map((item, index) => [
    index + 1,
    formatDate(item.createdAt),
    item.name || '',
    item.phone || '',
    getWalletLabel(item.wallet),
    Number(item.commitedAmount) || 0,
    item.description || '',
  ]);

  XLSX.utils.book_append_sheet(wb, buildDataSheet(columns, rows), 'Chi tiết giao dịch');

  writeWorkbook(wb, 'Lich_su_giao_dich_vi');
};

export const exportCashFlowToExcel = (data, filter) => {
  const wb = XLSX.utils.book_new();

  const days = data.days || [];
  const totals = data.totals || {};

  const infoRows = [
    ['Ngày xuất báo cáo:', formatDate(new Date())],
    ['Thời gian lọc:', formatFilterRange(filter.from, filter.to)],
    ['Khách hàng:', data.user ? `${data.user.name} - ${data.user.phone}` : 'Tất cả khách hàng'],
  ];

  const stats = [
    ['Tồn đầu ví chơi:', formatCurrency(Number(data.openMain))],
    ['Tồn đầu ví thưởng:', formatCurrency(Number(data.openReward))],
    [],
    ['Tổng nạp ví:', formatCurrency(Number(totals.charge))],
    ['Tổng trả thưởng:', formatCurrency(Number(totals.payReward))],
    ['Tổng hoàn / hủy vé:', formatCurrency(Number(totals.refundTicket))],
    ['Tổng mua vé:', formatCurrency(Number(totals.buy))],
    ['Tổng rút về TK NH:', formatCurrency(Number(totals.withdraw))],
    ['Tổng chuyển ví thưởng sang ví mua:', formatCurrency(Number(totals.transfer))],
    ['Khác / điều chỉnh ví chơi:', formatCurrency(Number(totals.otherMain))],
    ['Khác / điều chỉnh ví thưởng:', formatCurrency(Number(totals.otherReward))],
    [],
    ['Tồn cuối ví chơi:', formatCurrency(Number(data.closeMain))],
    ['Tồn cuối ví thưởng:', formatCurrency(Number(data.closeReward))],
  ];

  XLSX.utils.book_append_sheet(wb, buildReportSheet('BÁO CÁO DÒNG TIỀN KHÁCH HÀNG', infoRows, stats), 'Thống kê tổng quan');

  const groupHeader = [
    'Ngày',
    'Tồn đầu', '',
    'Tăng trong kỳ', '', '',
    'Giảm trong kỳ', '',
    'Chuyển ví thưởng sang ví mua',
    'Khác / Điều chỉnh', '',
    'Tồn cuối', '',
  ];

  const subHeader = [
    '',
    'Ví chơi', 'Ví thưởng',
    'Nạp ví', 'Trả thưởng', 'Hoàn / hủy vé',
    'Mua vé', 'Rút về TK NH',
    '',
    'Ví chơi', 'Ví thưởng',
    'Ví chơi', 'Ví thưởng',
  ];

  const toRowValues = (row) => [
    Number(row.openMain) || 0,
    Number(row.openReward) || 0,
    Number(row.charge) || 0,
    Number(row.payReward) || 0,
    Number(row.refundTicket) || 0,
    Number(row.buy) || 0,
    Number(row.withdraw) || 0,
    Number(row.transfer) || 0,
    Number(row.otherMain) || 0,
    Number(row.otherReward) || 0,
    Number(row.closeMain) || 0,
    Number(row.closeReward) || 0,
  ];

  const totalRow = [
    'TC',
    ...toRowValues({
      ...totals,
      openMain: data.openMain,
      openReward: data.openReward,
      closeMain: data.closeMain,
      closeReward: data.closeReward,
    }),
  ];

  const dataRows = days.map((row) => [
    row.date.split('-').reverse().join('/'),
    ...toRowValues(row),
  ]);

  const wsData = XLSX.utils.aoa_to_sheet([groupHeader, subHeader, totalRow, ...dataRows]);

  wsData['!cols'] = [
    { wch: 12 },
    { wch: 14 }, { wch: 14 },
    { wch: 14 }, { wch: 14 }, { wch: 14 },
    { wch: 14 }, { wch: 14 },
    { wch: 16 },
    { wch: 14 }, { wch: 14 },
    { wch: 14 }, { wch: 14 },
  ];

  wsData['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
    { s: { r: 0, c: 1 }, e: { r: 0, c: 2 } },
    { s: { r: 0, c: 3 }, e: { r: 0, c: 5 } },
    { s: { r: 0, c: 6 }, e: { r: 0, c: 7 } },
    { s: { r: 0, c: 8 }, e: { r: 1, c: 8 } },
    { s: { r: 0, c: 9 }, e: { r: 0, c: 10 } },
    { s: { r: 0, c: 11 }, e: { r: 0, c: 12 } },
  ];

  XLSX.utils.book_append_sheet(wb, wsData, 'Dòng tiền theo ngày');

  writeWorkbook(wb, 'Bao_cao_dong_tien');
};
